import { PageHero } from '@/components/PageHero';
import { YieldCurve } from '@/components/fx/Schematics';
import { TerminalPanel } from '@/components/fx/TerminalPanel';
import { Reveal } from '@/components/fx/Reveal';
import { Headline } from '@/components/fx/Headline';
import { TiltCard } from '@/components/fx/TiltCard';
import type { EngineVals } from '@/lib/vals/engine';

export function EnginePage({ v }: { v: EngineVals }) {
  return (
    <main id="wft-main" tabIndex={-1}>
      <PageHero
        eyebrow="GodEngine"
        title="Not one model. An engine with two ways of answering."
        highlight={['two', 'ways']}
        lede="TimesFM 2.5 produces the quantile forecast around a single series. The GodEngine swarm resolver answers the other half of the question — which futures are on the table, and how heavy each one is."
        aside={
          <TerminalPanel
            mnemonic="RTS"
            title="Curve forecast"
            sources={['FRED', 'US Treasury', 'GodEngine']}
          >
            <YieldCurve />
          </TerminalPanel>
        }
      />

      <section className="section">
        <div className="shell">
          <div className="lanes">
            {v.lanes.map((lane, i) => (
              <Reveal key={lane.name} delay={i * 120}>
                <TiltCard className="lane glass" max={4}>
                  <span className="lane__bar" style={{ background: lane.color }} />
                  <h2 className="lane__name" style={{ color: lane.color }}>
                    {lane.name}
                  </h2>
                  <code className="lane__module">{lane.module}</code>
                  <p className="lane__body">{lane.body}</p>
                  <p className="lane__feeds">
                    <span>Answers</span> {lane.answers}
                  </p>
                  <p className="lane__feeds">
                    <span>Feeds</span> {lane.feeds}
                  </p>
                </TiltCard>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* The path a forecast takes */}
      <section className="section">
        <div className="shell">
          <Reveal as="p" className="eyebrow">
            What happens when you ask for a forecast
          </Reveal>
          <Headline
            as="h2"
            className="display display--section section__title"
            text="Six steps, and what stops each one."
            highlight={['stops']}
          />

          <ol className="path">
            {v.path.map((step, i) => (
              <Reveal as="li" key={step.n} delay={i * 80} className="path__item">
                <div className="path__marker" aria-hidden="true">
                  <span>{step.n}</span>
                </div>
                <div className="path__body glass">
                  <h3>{step.step}</h3>
                  <p>{step.body}</p>
                  <p className="path__refuses">
                    <span>Refuses when</span> {step.refuses}
                  </p>
                </div>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      {/* Provenance */}
      <section className="section">
        <div className="shell split">
          <div className="split__lead">
            <Reveal as="p" className="eyebrow">
              What every forecast carries
            </Reveal>
            <Headline
              as="h2"
              className="display display--section"
              text="A plausible line is worse than no line."
              highlight={['worse']}
            />
            <Reveal as="p" className="lede" delay={200}>
              The decoder name travels to the browser with every forecast, so a run from the
              fallback decoder is visible rather than hidden — and it draws nothing at all.
            </Reveal>
          </div>

          <div className="fields">
            {v.provenance.map((f, i) => (
              <Reveal key={f.field} delay={i * 60}>
                <div className="field glass">
                  <code>{f.field}</code>
                  <p>{f.what}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Integrity */}
      <section className="section">
        <div className="shell">
          <Reveal as="p" className="eyebrow">
            Weight integrity
          </Reveal>
          <Headline
            as="h2"
            className="display display--section section__title"
            text="Vendored, not forked."
            highlight={['forked.']}
          />
          <div className="pipeline">
            {v.integrity.map((s, i) => (
              <Reveal key={s.n} delay={i * 90}>
                <TiltCard className="stage glass" max={4}>
                  <span className="stage__n" style={{ color: 'var(--engine)' }}>
                    {s.n}
                  </span>
                  <p className="stage__lead">{s.step}</p>
                  <code className="stage__note">{s.where}</code>
                </TiltCard>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Commitments */}
      <section className="section">
        <div className="shell">
          <Reveal as="p" className="eyebrow">
            Six commitments the engine makes
          </Reveal>
          <Headline
            as="h2"
            className="display display--section section__title"
            text="What the engine will never do."
            highlight={['never']}
          />
          <div className="clusters">
            {v.commitments.map((c, i) => (
              <Reveal key={c.label} delay={i * 70}>
                <TiltCard className="cluster glass" max={3}>
                  <div className="cluster__head">
                    <h3>{c.label}</h3>
                  </div>
                  <p className="cluster__blurb">{c.body}</p>
                </TiltCard>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Where it appears */}
      <section className="section">
        <div className="shell">
          <Reveal as="p" className="eyebrow">
            Where the engine appears
          </Reveal>
          <div className="appearances">
            {v.appearances.map((a, i) => (
              <Reveal key={a.mnemonic} delay={i * 60}>
                <div className="appearance glass">
                  <code>{a.mnemonic}</code>
                  <h3>{a.title}</h3>
                  <p>{a.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
